import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {catchError, exhaustMap, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import * as AuthActions from './auth.actions';
import {AuthService, PASSWORD_UPGRADED_MESSAGE, PasswordResetRequiredError, SCHOOL_ACCOUNT_CLOSED_MESSAGE} from '@core/services/auth.service';
import {Router} from '@angular/router';
import { UserAuthentication, UserToken } from '@core/models/domain/auth.domain';
import { buildAuthenticationFromToken } from './auth.reducer';
import { PersistencyService } from '@core/services/persistency.service';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private persistencyService: PersistencyService,
    private router: Router
  ) {}

  // LOGIN
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      // exhaustMap ignores further login dispatches while one is in flight,
      // so a double-click can't fire concurrent login requests.
      exhaustMap(({ request })=>
        this.authService.login(request).pipe(
          map((userAuth: UserAuthentication) => {
            if (userAuth && userAuth.token) {
              return AuthActions.loginSuccess({ authentication: userAuth });
            } else {
              return AuthActions.loginFailure({ error: "Login request failed" });
            }
          }),
          catchError(err => {
            console.error('Login request error:', err);
            if (err instanceof PasswordResetRequiredError) {
              return of(AuthActions.loginFailure({ error: PASSWORD_UPGRADED_MESSAGE }));
            }
            // A deactivated school (DIGI-1855): say so, rather than blaming the credentials.
            if (err?.message === SCHOOL_ACCOUNT_CLOSED_MESSAGE) {
              return of(AuthActions.loginFailure({ error: SCHOOL_ACCOUNT_CLOSED_MESSAGE }));
            }
            return of(AuthActions.loginFailure({ error: 'Unable to sign in. Please check your credentials and try again.' }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ authentication }) => {
        // Access token intentionally NOT persisted — lives in NgRx memory only (OWASP A03)
        this.persistencyService.saveUserAccountId(authentication.userAccountId);
        this.persistencyService.saveSchoolAccountId(authentication.schoolAccountId);
        this.router.navigate(["/dashboards"]);
      })
    ), { dispatch: false }
  );

  // CREATE SCHOOL ACCOUNT
  createSchoolAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.createSchoolAccount),
      mergeMap(({ request }) =>
        this.authService.createSchoolAccount(request).pipe(
          map((res: UserToken) => {
            if (res && res.token) {
              return AuthActions.createSchoolAccountSuccess({ token: res });
            } else {
              return AuthActions.createSchoolAccountFailure({ error: "Account creation request failed" });
            }
          }),
          catchError(err => of(AuthActions.createSchoolAccountFailure({ error: err.message })))
        )
      )
    )
  );

  createSchoolAccountSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.createSchoolAccountSuccess),
      tap(({ token }) => this.persistToken(token))
    ), { dispatch: false }
  );

  // RESEND VALIDATION CODE
  resendValidationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resendValidationCode),
      mergeMap(() =>
        this.authService.resendValidationCode().pipe(
          map((res: UserToken) => {
            if (res && res.token) {
              return AuthActions.resendValidationCodeSuccess({ token: res});
            } else {
              return AuthActions.resendValidationCodeFailure({ error: "Resend Validation code failed" });
            }
          }),
          catchError(err => of(AuthActions.resendValidationCodeFailure({ error: err.message })))
        )
      )
    )
  );

  resendValidationCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resendValidationCodeSuccess),
      tap(({ token }) => this.persistToken(token))
    ), { dispatch: false }
  );

  // VALIDATE CODE
  validateCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateCode),
      mergeMap(({ request }) =>
        this.authService.validateCode(request).pipe(
          map((res: UserToken) => {
            if (res && res.token) {
              return AuthActions.validateCodeSuccess({ token: res});
            } else {
              return AuthActions.validateCodeFailure({ error: "Validation code failed" });
            }
          }),
          catchError(err => of(AuthActions.validateCodeFailure({ error: err.message })))
        )
      )
    )
  );

  validateCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateCodeSuccess),
       tap(({ token }) => this.persistToken(token))
    ), { dispatch: false }
  );

  // RESET PASSWORD REQUEST
  requestPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestPasswordReset),
      mergeMap(({ request }) =>
        this.authService.requestPasswordReset(request).pipe(
          map((res: UserToken) => {
            if (res && res.token) {
              return AuthActions.requestPasswordResetSuccess({ token: res });
            } else {
              return AuthActions.requestPasswordResetFailure({ error: "Password reset request failed" });
            }
          }),
          catchError(err => of(AuthActions.requestPasswordResetFailure({ error: err.message })))
        )
      )
    )
  );

  requestPasswordResetSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestPasswordResetSuccess),
       tap(({ token }) => this.persistToken(token))
    ), { dispatch: false }
  );

  // SET PASSWORD
  setPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setPassword),
      mergeMap(({ request }) =>
        this.authService.setPassword(request).pipe(
          map((res: string) => {
            if (res) {
                return AuthActions.setPasswordSuccess();
            } else {
              return AuthActions.setPasswordFailure({ error: "Modify password failed" });
            }
          }),
          catchError(err => of(AuthActions.setPasswordFailure({ error: err.message })))
        )
      )
    )
  );

  // CHANGE PASSWORD
  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      mergeMap(({ request }) =>
        this.authService.changePassword(request).pipe(
          map((res: string) => {
            if (res) {
              return AuthActions.changePasswordSuccess();
            } else {
              return AuthActions.changePasswordFailure({ error: "Password change failed" });
            }
          }),
          catchError(err => of(AuthActions.changePasswordFailure({ error: err.message })))
        )
      )
    )
  );

  // LOGOUT — must call backend to clear the HttpOnly refresh token cookie
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap(() =>
        this.authService.logoutSession().pipe(
          catchError(() => of(null)) // fire-and-forget: clear client state regardless
        )
      ),
      tap(() => {
        this.clearToken();
        this.router.navigate(['/auth/login']);
      })
    ), { dispatch: false }
  );

  // HYDRATE AUTH STATE — call /auth/refresh on app init; browser sends HttpOnly cookie automatically
  hydrateAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.hydrateAuth),
      switchMap(() => {
        const userAccountId = this.persistencyService.getUserAccountId();
        const schoolAccountId = this.persistencyService.getSchoolAccountId();

        return this.authService.refreshToken().pipe(
          map((result: UserToken) => {
            // Prefer IDs returned by /refresh (works even when storage is disabled);
            // fall back to the storage cache only if the backend did not return them.
            const authentication: UserAuthentication =
              buildAuthenticationFromToken(result, userAccountId ?? '', schoolAccountId ?? '');
            return AuthActions.hydrateAuthSuccess({ authentication });
          }),
          catchError(() => of(AuthActions.hydrateAuthFailure()))
        );
      })
    )
  );

  // REFRESH TOKEN — called by interceptor on 401 or programmatically.
  // exhaustMap ignores duplicate dispatches while a refresh is in flight: switchMap would
  // cancel the running call, and the backend rotates the refresh cookie on every attempt,
  // so a cancelled call can invalidate the cookie the retry then presents.
  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshToken),
      exhaustMap(() =>
        this.authService.refreshToken().pipe(
          map((result: UserToken) => AuthActions.refreshTokenSuccess({ token: result })),
          catchError((err) => of(AuthActions.refreshTokenFailure({ error: err.message })))
        )
      )
    )
  );

  hydrateAuthSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.hydrateAuthSuccess),
      tap(() => {
        // Mark user as logged in. isAdmin is an optimistic default: the restored session carries
        // no role, and MainLayoutComponent corrects the flag from schoolUserType once the user loads.
        this.authService.isUserLoggedIn = true;
        this.authService.isAdmin = true;
      })
    ), { dispatch: false }
  );

  private persistToken(token: UserToken): void {
    // Access token intentionally NOT persisted — lives in NgRx memory only (OWASP A03)
    this.persistencyService.saveUserAccountId(token.userAccountId);
    this.persistencyService.saveSchoolAccountId(token.schoolAccountId);
  }

  private clearToken(): void {
    this.persistencyService.removeUserAccountId();
    this.persistencyService.removeSchoolAccountId();
  }
}
